"use client";
import React from "react";

import { Box } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { TextFieldProps } from "../TextField";

export interface RadioGroupProps
  extends Omit<TextFieldProps, "error" | "value" | "label"> {
  children: React.ReactElement | React.ReactElement[] | undefined;
  error?: boolean;
  label?: Exclude<React.ReactNode, undefined | null | boolean>;
  value?: string | number;
}

export interface RadioGroupContextProps {
  selectedValue?: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: boolean;
}

export const RadioGroupContext = React.createContext<
  RadioGroupContextProps | undefined
>(undefined);

export const RadioGroup: React.FC<RadioGroupProps> = (
  props: RadioGroupProps
) => {
  const { children, disabled, error, label, value, onChange } = props;
  const [selectedValue, setSelectedValue] = React.useState(value || "");

  const labelNode = React.useMemo(() => {
    return typeof label === "string" ? <Text>{label}</Text> : label;
  }, [label]);

  const onClickOption = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedValue(event.target.value);
      if (onChange) {
        onChange(event);
      }
    },
    [onChange]
  );

  const contextValue = {
    selectedValue,
    onChange: onClickOption,
    disabled,
    error
  };

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingX: "spacing-4"
        }}
      >
        <Box
          sx={{
            fontSize: "font-size-4",
            color: "text-default",
            paddingY: "spacing-3",
            fontWeight: "font-weight-medium",
            lineHeight: "leading-2",
            letterSpacing: "kerning-none"
          }}
        >
          {labelNode}
        </Box>
        {children}
      </Box>
    </RadioGroupContext.Provider>
  );
};
